import { productsApi } from "@/modules/products/presentation/api/client";
import type { FilterConditionDTO } from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
} from "@/shared/contracts/query-helpers";
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useDebounceCallback,
} from "@devhop/ui";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";

export function ReviewsFilter() {
  const nav = useNavigate({ from: "/app/reviews" });
  const search = useSearch({ from: "/app/reviews" }) as {
    limit?: number;
    offset?: number;
    sort?: Array<{ field: string; dir: "asc" | "desc" }> | string;
    filters?: FilterConditionDTO[] | string;
  };

  const products = useQuery({
    queryKey: ["products", "for-reviews-filter"],
    queryFn: () =>
      productsApi.list({
        limit: 100,
        offset: 0,
        status: "active",
      }),
  });
  const productOptions = products.data?.data ?? [];

  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];
  const contentFilter = findCondition(filters, "content");
  const productIdFilter = findCondition(filters, "productId");
  const hiddenFilter = findCondition(filters, "hidden");
  const ratingFilter = findCondition(filters, "rating");

  const [searchValue, setSearchValue] = useState<string>(
    (contentFilter?.value as string) || "",
  );

  useEffect(
    () => setSearchValue((contentFilter?.value as string) || ""),
    [contentFilter?.value],
  );

  const debounced = useDebounceCallback((val: string) => {
    let nextFilters = removeConditions(filters, "content") ?? [];
    if (val) {
      nextFilters = upsertCondition(nextFilters, {
        field: "content",
        op: "contains",
        value: val,
      });
    }
    nav({
      search: {
        ...search,
        offset: 0,
        filters: nextFilters?.length ? nextFilters : undefined,
      } as Record<string, unknown>,
    });
  }, 400);

  const handleProductChange = (value: string) => {
    let nextFilters = removeConditions(filters, "productId") ?? [];
    if (value && value !== "all") {
      nextFilters = upsertCondition(nextFilters, {
        field: "productId",
        op: "eq",
        value,
      });
    }
    nav({
      search: {
        ...search,
        offset: 0,
        filters: nextFilters?.length ? nextFilters : undefined,
      } as Record<string, unknown>,
    });
  };

  const handleHiddenChange = (value: string) => {
    let nextFilters = removeConditions(filters, "hidden") ?? [];
    if (value === "hidden") {
      nextFilters = upsertCondition(nextFilters, {
        field: "hidden",
        op: "eq",
        value: true,
      });
    } else if (value === "visible") {
      nextFilters = upsertCondition(nextFilters, {
        field: "hidden",
        op: "eq",
        value: false,
      });
    }
    nav({
      search: {
        ...search,
        offset: 0,
        filters: nextFilters?.length ? nextFilters : undefined,
      } as Record<string, unknown>,
    });
  };

  const handleRatingChange = (value: string) => {
    let nextFilters = removeConditions(filters, "rating") ?? [];
    if (value && value !== "all") {
      nextFilters = upsertCondition(nextFilters, {
        field: "rating",
        op: "eq",
        value: Number(value),
      });
    }
    nav({
      search: {
        ...search,
        offset: 0,
        filters: nextFilters?.length ? nextFilters : undefined,
      } as Record<string, unknown>,
    });
  };

  const clearFilters = () => {
    nav({
      search: { ...search, offset: 0, filters: undefined } as Record<
        string,
        unknown
      >,
    });
  };

  const productValue = (productIdFilter?.value as string) ?? "";
  const hiddenValue =
    hiddenFilter?.value === true
      ? "hidden"
      : hiddenFilter?.value === false
        ? "visible"
        : "all";
  const ratingValue = (ratingFilter?.value as number)?.toString() ?? "all";

  const hasFilter = filters.length > 0;

  function productName(p: { name?: unknown }) {
    if (!p.name || typeof p.name !== "object") return "—";
    const n = p.name as { lo?: string; th?: string; en?: string };
    return n.lo ?? n.th ?? n.en ?? "—";
  }

  return (
    <div className="mb-4 flex flex-wrap items-center gap-4 px-2">
      <Input
        placeholder="ຄົ້ນຫາໃນເນື້ອໃນ..."
        defaultValue={searchValue}
        onChange={(e) => debounced(e.target.value)}
        className="h-8 sm:max-w-2xs"
      />
      <Select value={productValue || "all"} onValueChange={handleProductChange}>
        <SelectTrigger className="h-8 w-[180px]">
          <SelectValue placeholder="ສິນຄ້າ" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">ທັງໝົດ</SelectItem>
          {productOptions.map((p) => (
            <SelectItem key={p.id} value={p.id}>
              {productName(p)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={hiddenValue} onValueChange={handleHiddenChange}>
        <SelectTrigger className="h-8 w-[140px]">
          <SelectValue placeholder="ສະຖານະ" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">ທັງໝົດ</SelectItem>
          <SelectItem value="visible">ສະແດງຢູ່</SelectItem>
          <SelectItem value="hidden">ຖືກຊ້ອນ</SelectItem>
        </SelectContent>
      </Select>
      <Select value={ratingValue} onValueChange={handleRatingChange}>
        <SelectTrigger className="h-8 w-[100px]">
          <SelectValue placeholder="ຄະແນນ" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">ທັງໝົດ</SelectItem>
          {[5, 4, 3, 2, 1].map((r) => (
            <SelectItem key={r} value={String(r)}>
              {r} ດາວ
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {hasFilter && (
        <Button variant="ghost" size="sm" onClick={clearFilters}>
          <XIcon className="h-4 w-4" /> ລ້າງຕົວກອງ
        </Button>
      )}
    </div>
  );
}
